import React, { useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Group,
    MantineProvider,
    Modal,
    NumberInput,
    Paper,
    Select,
    Stack,
    Stepper,
    Text,
    Textarea,
    TextInput,
    Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { useMediaQuery } from "@mantine/hooks";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { HotelDto } from "../../api/hotelBookingApi";
import { formatMoney, getNights, toYmd } from "../../utils/hotelBookingUtils";
import BookingSummary from "./BookingSummary";
import HotelSelector from "./HotelSelector";
import RoomBookingTable from "./RoomBookingTable";
import {
    AddRoomForm,
    BookingRoomRow,
    CreateHotelBookingFormPayload,
    HotelBookingTexts
} from "./HotelBookingTypes";
import { WHATSAPP_COUNTRIES } from "../../data/whatsappCountries";

type Props = {
    hotels: HotelDto[];
    saving: boolean;
    t: HotelBookingTexts;
    language: "en" | "da";
    onSubmit: (payload: CreateHotelBookingFormPayload) => Promise<boolean>;
    onFinished?: () => void;
};

export default function CreateBookingForm({
    hotels,
    saving,
    t,
    language,
    onSubmit,
    onFinished,
}: Props) {
    const isMobile = useMediaQuery("(max-width: 768px)");
    const [active, setActive] = useState(0);
    const [thankYouOpen, setThankYouOpen] = useState(false);

    const [selectedHotelId, setSelectedHotelId] = useState<number | null>(null);
    const [country, setCountry] = useState("");
    const [contactName, setContactName] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [comment, setComment] = useState("");

    const [whatsappCountryIso2, setWhatsappCountryIso2] = useState<string | null>(null);
    const [whatsappCountryCode, setWhatsappCountryCode] = useState("");
    const [whatsappNumber, setWhatsappNumber] = useState("");

    const PRIORITY_ISO2 = [
        "DK", "CA", "CN", "FR", "DE", "HK", "IN", "ID", "JP",
        "MY", "MX", "SG", "ES", "TW", "TH", "TR", "GB", "VN", "BG",
    ];

    const whatsappCountryOptions = useMemo(() => {
        const prioritySet = new Set(PRIORITY_ISO2);

        const popularItems = WHATSAPP_COUNTRIES
            .filter((c) => prioritySet.has(c.iso2))
            .sort((a, b) => PRIORITY_ISO2.indexOf(a.iso2) - PRIORITY_ISO2.indexOf(b.iso2))
            .map((c) => ({
                value: c.iso2,
                label: `${c.code} — ${c.country}`,
            }));

        const restItems = WHATSAPP_COUNTRIES
            .filter((c) => !prioritySet.has(c.iso2))
            .sort((a, b) => a.country.localeCompare(b.country))
            .map((c) => ({
                value: c.iso2,
                label: `${c.code} — ${c.country}`,
            }));

        return [
            { group: language === "en" ? "Popular" : "Populære", items: popularItems },
            { group: language === "en" ? "All countries" : "Alle lande", items: restItems },
        ];
    }, [language]);

    const selectedWhatsappCountry =
        WHATSAPP_COUNTRIES.find((x) => x.code === whatsappCountryCode)?.country ?? null;

    const whatsappValid =
        !!whatsappCountryCode &&
        !!whatsappNumber &&
        whatsappNumber.replace(/\s+/g, "").length >= 6;

    const [addRoomForm, setAddRoomForm] = useState<AddRoomForm>({
        hotelRoomTypeID: null,
        checkInDate: null,
        checkOutDate: null,
        guestsCount: 1,
    });

    const [rooms, setRooms] = useState<BookingRoomRow[]>([]);

    const selectedHotel = useMemo(
        () => hotels.find((x) => x.hotelID === selectedHotelId) ?? null,
        [hotels, selectedHotelId]
    );

    const bookingStartDate = useMemo(
        () => selectedHotel?.startBooking ? new Date(selectedHotel.startBooking) : null,
        [selectedHotel]
    );

    const bookingEndDate = useMemo(
        () => selectedHotel?.endBooking ? new Date(selectedHotel.endBooking) : null,
        [selectedHotel]
    );

    function isDateOutsideBookingWindow(date: Date) {
        if (bookingStartDate && date < bookingStartDate) return true;
        if (bookingEndDate && date > bookingEndDate) return true;

        return false;
    }

    const roomTypeSelectData = useMemo(
        () =>
            selectedHotel?.roomTypes
                ?.filter((r) => r.isActive)
                .map((r) => ({
                    value: String(r.hotelRoomTypeID),
                    label: `${r.name} · ${formatMoney(r.pricePerNight)} / night`,
                })) ?? [],
        [selectedHotel]
    );

    function emailValid(value: string) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
    }

    function resetSelectedHotel(hotelId: number | null) {
        const hotel = hotels.find((x) => x.hotelID === hotelId) ?? null;

        const startDate = hotel?.startBooking ? new Date(hotel.startBooking) : null;
        const endDate = hotel?.endBooking ? new Date(hotel.endBooking) : null;

        setSelectedHotelId(hotelId);
        setRooms([]);
        setAddRoomForm({
            hotelRoomTypeID: null,
            checkInDate: startDate,
            checkOutDate: endDate,
            guestsCount: 1,
        });
    }

    function validateAddRoom(): string | null {
        if (!selectedHotel) return t.chooseHotel;
        if (!addRoomForm.hotelRoomTypeID) return t.chooseType;
        if (!addRoomForm.checkInDate) return t.checkIn;
        if (!addRoomForm.checkOutDate) return t.checkOut;

        if (addRoomForm.checkOutDate <= addRoomForm.checkInDate) {
            return language === "en"
                ? "Check-out must be after check-in"
                : "Check-out skal være efter check-in";
        }
        if (bookingStartDate && addRoomForm.checkInDate < bookingStartDate) {
            return language === "en"
                ? "Check-in cannot be before booking start date"
                : "Check-in kan ikke være før booking startdato";
        }

        if (bookingEndDate && addRoomForm.checkOutDate > bookingEndDate) {
            return language === "en"
                ? "Check-out cannot be after booking end date"
                : "Check-out kan ikke være efter booking slutdato";
        }

        const roomType = selectedHotel.roomTypes.find(
            (r) => r.hotelRoomTypeID === addRoomForm.hotelRoomTypeID
        );

        if (!roomType) return t.chooseType;

        if (addRoomForm.guestsCount > roomType.capacity) {
            return language === "en"
                ? `${roomType.name} allows maximum ${roomType.capacity} guests`
                : `${roomType.name} tillader max ${roomType.capacity} gæster`;
        }

        return null;
    }

    function addRoomToList() {
        const error = validateAddRoom();

        if (error) {
            notifications.show({ color: "red", title: t.missing, message: error });
            return;
        }

        const roomType = selectedHotel!.roomTypes.find(
            (r) => r.hotelRoomTypeID === addRoomForm.hotelRoomTypeID
        )!;

        const nights = getNights(addRoomForm.checkInDate!, addRoomForm.checkOutDate!);

        setRooms((prev) => [
            ...prev,
            {
                id: crypto.randomUUID(),
                hotelRoomTypeID: roomType.hotelRoomTypeID,
                roomTypeName: roomType.name,
                checkInDate: addRoomForm.checkInDate!,
                checkOutDate: addRoomForm.checkOutDate!,
                guestsCount: addRoomForm.guestsCount,
                pricePerNight: roomType.pricePerNight,
                nights,
                totalPrice: nights * roomType.pricePerNight,
            },
        ]);

        setAddRoomForm({
            hotelRoomTypeID: null,
            checkInDate: addRoomForm.checkInDate,
            checkOutDate: addRoomForm.checkOutDate,
            guestsCount: 1,
        });
    }

    function removeRoom(id: string) {
        setRooms((prev) => prev.filter((x) => x.id !== id));
    }

    function isStepValid(step: number) {
        switch (step) {
            case 0:
                return !!selectedHotelId;
            case 1:
                return (
                    country.trim().length > 0 &&
                    contactName.trim().length > 0 &&
                    emailValid(contactEmail) &&
                    whatsappValid
                );
            case 2:
                return rooms.length > 0;
            case 3:
                return true;
            default:
                return true;
        }
    }

    function next() {
        if (isStepValid(active)) {
            setActive((current) => Math.min(current + 1, 4));
            return;
        }

        notifications.show({
            color: "red",
            title: t.missing,
            message:
                language === "en"
                    ? "Please fill in the required information before continuing."
                    : "Udfyld de nødvendige oplysninger før du fortsætter.",
        });
    }

    function prev() {
        setActive((current) => Math.max(current - 1, 0));
    }

    async function handleSubmit() {
        if (!isStepValid(0) || !isStepValid(1) || !isStepValid(2)) {
            notifications.show({
                color: "red",
                title: t.missing,
                message:
                    language === "en"
                        ? "Please go back and fill in all required information."
                        : "Gå tilbage og udfyld alle nødvendige oplysninger.",
            });
            return;
        }

        const success = await onSubmit({
            hotelID: selectedHotelId!,
            country: country.trim(),
            contactName: contactName.trim(),
            contactEmail: contactEmail.trim(),
            whatsappCountryCode: whatsappCountryCode,
            whatsappNumber: whatsappNumber.trim(),
            comment: comment.trim(),
            rooms: rooms.map((row) => ({
                hotelRoomTypeID: row.hotelRoomTypeID,
                checkInDate: toYmd(row.checkInDate),
                checkOutDate: toYmd(row.checkOutDate),
                guestsCount: row.guestsCount,
            })),
        });

        if (success) {
            setThankYouOpen(true);
        }
    }

    function closeThankYou() {
        setThankYouOpen(false);

        setActive(0);
        setSelectedHotelId(null);
        setCountry("");
        setContactName("");
        setContactEmail("");
        setComment("");
        setRooms([]);
        setAddRoomForm({
            hotelRoomTypeID: null,
            checkInDate: null,
            checkOutDate: null,
            guestsCount: 1,
        });
        setWhatsappCountryIso2(null);
        setWhatsappCountryCode("");
        setWhatsappNumber("");

        onFinished?.();
    }

    return (
        <Stack gap="md">
            <MantineProvider
                theme={{
                    primaryColor: "brand",
                    colors: {
                        brand: [
                            "#ffe5e5",
                            "#ffcaca",
                            "#ff9a9a",
                            "#ff7a7a",
                            "#ff6868",
                            "#ff5e5e",
                            "#f24848",
                            "#e53a3a",
                            "#d92d2d",
                            "#cc2222",
                        ],
                    },
                }}
            >
                <Stepper
                    active={active}
                    allowNextStepsSelect={false}
                    orientation={isMobile ? "vertical" : "horizontal"}
                    size={isMobile ? "sm" : "md"}
                    color="brand"
                >
                    <Stepper.Step label={t.hotel}>
                        <Stack gap="md">
                            <Paper withBorder p="lg" radius="md">
                                <Group gap="sm">
                                    <Badge size="xl" radius="xl" color="brand" variant="filled">
                                        1
                                    </Badge>
                                    <Stack gap={0}>
                                        <Title order={3}>{t.chooseHotel}</Title>
                                        <Text c="dimmed" size="sm">
                                            {t.chooseHotelText}
                                        </Text>
                                    </Stack>
                                </Group>
                            </Paper>

                            <HotelSelector
                                hotels={hotels}
                                selectedHotelId={selectedHotelId}
                                onSelectHotel={resetSelectedHotel}
                                t={t}
                            />
                        </Stack>
                    </Stepper.Step>

                    <Stepper.Step label={t.contactInfo}>
                        <Paper withBorder p="lg" radius="md">
                            <Stack gap="md">
                                <Title order={4}>{t.contactInfo}</Title>

                                <Group grow>
                                    <TextInput
                                        label={t.country}
                                        placeholder="Denmark"
                                        value={country}
                                        onChange={(e) => setCountry(e.currentTarget.value)}
                                        required
                                    />
                                    <TextInput
                                        label={t.contactPerson}
                                        placeholder="Name"
                                        value={contactName}
                                        onChange={(e) => setContactName(e.currentTarget.value)}
                                        required
                                    />
                                    <TextInput
                                        label={t.contactEmail}
                                        placeholder="mail@example.com"
                                        value={contactEmail}
                                        onChange={(e) => setContactEmail(e.currentTarget.value)}
                                        required
                                        type="email"
                                        error={
                                            contactEmail && !emailValid(contactEmail)
                                                ? language === "en"
                                                    ? "Invalid email"
                                                    : "Ugyldig email"
                                                : null
                                        }
                                    />
                                    <Group grow>
                                        <Select
                                            label="WhatsApp landekode"
                                            placeholder="Vælg landekode"
                                            value={whatsappCountryIso2}
                                            onChange={(iso2) => {
                                                const c = WHATSAPP_COUNTRIES.find((x) => x.iso2 === iso2);

                                                setWhatsappCountryIso2(iso2);
                                                setWhatsappCountryCode(c?.code ?? "");
                                            }}
                                            data={whatsappCountryOptions}
                                            searchable
                                            required
                                        />

                                        <TextInput
                                            label="WhatsApp-nummer"
                                            placeholder="f.eks. 12345678"
                                            value={whatsappNumber}
                                            onChange={(e) => {
                                                const cleaned = e.currentTarget.value.replace(/[^\d\s-]/g, "");
                                                setWhatsappNumber(cleaned);
                                            }}
                                            required
                                            error={
                                                whatsappNumber && !whatsappValid
                                                    ? language === "en"
                                                        ? "Enter a valid WhatsApp number"
                                                        : "Indtast et gyldigt WhatsApp-nummer"
                                                    : null
                                            }
                                        />
                                    </Group>                                 
                                </Group>
                            </Stack>
                        </Paper>
                    </Stepper.Step>

                    <Stepper.Step label={t.rooms}>
                        <Stack gap="md">
                            <Paper withBorder p="lg" radius="md">
                                <Stack gap="md">
                                    <Title order={4}>{t.addRoom}</Title>

                                    <Group grow align="flex-end">
                                        <Select
                                            label={t.roomType}
                                            placeholder={t.chooseType}
                                            data={roomTypeSelectData}
                                            value={
                                                addRoomForm.hotelRoomTypeID
                                                    ? String(addRoomForm.hotelRoomTypeID)
                                                    : null
                                            }
                                            onChange={(v) =>
                                                setAddRoomForm((prev) => ({
                                                    ...prev,
                                                    hotelRoomTypeID: v ? Number(v) : null,
                                                }))
                                            }
                                            searchable
                                            disabled={!selectedHotel}
                                        />

                                        <DateInput
                                            label={t.checkIn}
                                            value={addRoomForm.checkInDate}
                                            onChange={(v) =>
                                                setAddRoomForm((prev) => ({
                                                    ...prev,
                                                    checkInDate: v,
                                                }))
                                            }
                                            minDate={bookingStartDate ?? undefined}
                                            maxDate={bookingEndDate ?? undefined}
                                            excludeDate={isDateOutsideBookingWindow}
                                            clearable
                                        />

                                        <DateInput
                                            label={t.checkOut}
                                            value={addRoomForm.checkOutDate}
                                            onChange={(v) =>
                                                setAddRoomForm((prev) => ({
                                                    ...prev,
                                                    checkOutDate: v,
                                                }))
                                            }
                                            minDate={bookingStartDate ?? undefined}
                                            maxDate={bookingEndDate ?? undefined}
                                            excludeDate={isDateOutsideBookingWindow}
                                            clearable
                                        />

                                        <NumberInput
                                            label={t.guests}
                                            value={addRoomForm.guestsCount}
                                            onChange={(v) =>
                                                setAddRoomForm((prev) => ({
                                                    ...prev,
                                                    guestsCount: Number(v || 1),
                                                }))
                                            }
                                            min={1}
                                            max={10}
                                        />

                                        <Button color="brand" onClick={addRoomToList} disabled={!selectedHotel}>
                                            {t.addToList}
                                        </Button>
                                    </Group>

                                    <RoomBookingTable rooms={rooms} onRemoveRoom={removeRoom} t={t} />
                                </Stack>
                            </Paper>

                            <BookingSummary rooms={rooms} t={t} />
                        </Stack>
                    </Stepper.Step>

                    <Stepper.Step label="Review">
                        <Stack gap="md">
                            <Paper withBorder p="lg" radius="md">
                                <Textarea
                                    label={`${t.comments} (optional)`}
                                    placeholder={t.commentsPlaceholder}
                                    value={comment}
                                    onChange={(e) => setComment(e.currentTarget.value)}
                                    minRows={3}
                                />
                            </Paper>

                            <BookingSummary rooms={rooms} t={t} />

                            <Alert icon={<IconAlertCircle size={18} />} color="brand" variant="light">
                                <Stack gap={4}>
                                    <Text size="sm">{t.submitInfo}</Text>
                                    <Text size="sm">
                                        <b>{t.hotel}:</b> {selectedHotel?.name ?? "-"}
                                    </Text>
                                    <Text size="sm">
                                        <b>{t.contactPerson}:</b> {contactName}
                                    </Text>
                                    <Text size="sm">
                                        <b>{t.contactEmail}:</b> {contactEmail}
                                    </Text>
                                    <Text size="sm">
                                        <b>Whatsapp:</b> {whatsappCountryCode}{whatsappNumber}
                                    </Text>
                                    <Text size="sm">
                                        <b>{t.rooms}:</b> {rooms.length}
                                    </Text>
                                </Stack>
                            </Alert>
                        </Stack>
                    </Stepper.Step>
                </Stepper>

                {active < 4 && (
                    <Group justify="space-between" mt="md">
                        <Button variant="default" onClick={prev} disabled={active === 0}>
                            Back
                        </Button>

                        {active === 3 ? (
                            <Button
                                color="green"
                                loading={saving}
                                onClick={handleSubmit}
                                leftSection={<IconCheck size={16} />}
                            >
                                {t.submit}
                            </Button>
                        ) : (
                            <Button color="brand" onClick={next} disabled={!isStepValid(active)}>
                                Next
                            </Button>
                        )}
                    </Group>
                )}

                <Modal
                    opened={thankYouOpen}
                    onClose={closeThankYou}
                    title={language === "en" ? "Thank you for your booking" : "Tak for din booking"}
                    centered
                >
                    <Stack gap="sm">
                        <Text fw={700}>
                            {language === "en"
                                ? "Your hotel booking has been received."
                                : "Din hotelbooking er modtaget."}
                        </Text>

                        <Text>
                            {language === "en"
                                ? "Our event team will now review your booking request and room requirements."
                                : "Vores eventteam vil nu gennemgå din booking og dine værelsesønsker."}
                        </Text>

                        <Text>
                            {language === "en"
                                ? "Once your booking has been reviewed and approved, you will receive more information about payment and the next steps."
                                : "Når din booking er gennemgået og godkendt, modtager du mere information om betaling og de næste trin."}
                        </Text>

                        <Text size="sm" c="dimmed">
                            {language === "en"
                                ? "A confirmation email with your booking information will also be sent to your contact email."
                                : "En bekræftelsesmail med dine bookingoplysninger vil også blive sendt til din kontaktmail."}
                        </Text>

                        <Button color="brand" fullWidth onClick={closeThankYou}>
                            OK
                        </Button>
                    </Stack>
                </Modal>
            </MantineProvider>
        </Stack>
    );
}